import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import TabControllerContext from './TabControllerContext';

function useUniquePrefix() {
  const [id, setId] = React.useState(null);
  React.useEffect(() => {
    setId(`sui-${nanoid()}`);
  }, []);
  return id;
}

/**
 * `TabController` is a wrapper for `TabList` and `TabPanel`s to more easily implement fully accessible tabs.
 *
 * Related components: [TabList](#tablist), [TabPanel](#tabpanel), [Tab](#tab)
 *
 * Usage:
 *
 * ```jsx
 * import { TabController } from '@one-thd/sui-atomic-components';
 * ```
 */
const TabController = (props) => {

  const { children, value } = props;
  const idPrefix = useUniquePrefix();

  const context = React.useMemo(() => {
    return { idPrefix, value };
  }, [idPrefix, value]);

  return (
    <TabControllerContext.Provider value={context}>
      {children}
    </TabControllerContext.Provider>
  );
};

TabController.displayName = 'TabController';

TabController.propTypes = {
  /**
   * The Content inside the component.
   */
  children: PropTypes.node,
  /**
   * Initial value the controlled tabs will be set to initially.
   */
  value: PropTypes.string
};

export { TabController };
